import {type DataColumnOptionSet} from "@hantera/design-system";
import {Models} from "@hantera/portal-app";
import {Query, QueryNavigation} from "@/backend/queries";
import useGraphPhrases from "@/composition/use-graph-phrases";
import {ColumnMap} from "./table-models";
import log from "@/log";

export default function getColumnOptions(
  graphMeta: Record<string, Models.GraphMeta.GraphNodeMeta>,
  graphPhrases: ReturnType<typeof useGraphPhrases>,
  columnMap: ColumnMap[],
  query: Query
) {
  const result: DataColumnOptionSet[] = [];

  const selectedColumns = columnMap.reduce((res, map) => {
    res[map.column.queryPath.join('.') + '.' + map.column.fieldName] = map.column.queryColumnId
    return res
  }, {} as Record<string, string>)
  
  from(
    Object.keys(graphMeta).reduce((res, nodeKey) => {
      const node = graphMeta[nodeKey];
      res[node.rootEdgeName] = node;
      return res;
    }, {} as Record<string, Models.GraphMeta.GraphNodeMeta>),
    query.navigation
  );

  return result;

  function from(edges: Record<string, Models.GraphMeta.GraphNodeMeta>, navigation: QueryNavigation, querySource: string[] = []) {
    if (!navigation.edge) { return; }

    const meta = edges[navigation.edge!];

    if (!meta) { return; }
    
    const setQuerySource = querySource.concat(navigation.edge)
    const set = {
      setLabel: queryPathPhrase(graphMeta, graphPhrases, setQuerySource).join(' / '),
      columns: []
    } as DataColumnOptionSet;

    for (const fieldKey in meta.fields) {

      const field = meta.fields[fieldKey]
      const valueType = meta.fields[fieldKey].valueType;
      if (valueType.type === 'dynamicFields') {
        for (const dynamicFieldKey of valueType.fields) {
          const key = selectedColumns[setQuerySource.join('.') + `.${fieldKey}->'${dynamicFieldKey}'`] ?? (setQuerySource.join('.') + `.${fieldKey}->'${dynamicFieldKey}'`)
          set.columns.push({
            key: key,
            label: graphPhrases.field(getColumnNode(graphMeta, setQuerySource), `${fieldKey}.${dynamicFieldKey}`),
            _add() {
              log.debug('adding column', key, setQuerySource)
              query.columns.push({
                queryColumnId: key,
                queryPath: setQuerySource,
                fieldName: `${fieldKey}->'${dynamicFieldKey}'`
              })
            }
          } as any);
        }
      } else {
        const key = selectedColumns[setQuerySource.join('.') + `.${fieldKey}`] ?? (setQuerySource.join('.') + `.${fieldKey}`)
        
        set.columns.push({
          key,
          label: getColumnLabel(getColumnNode(graphMeta, setQuerySource), fieldKey, field),
          _add() {
            log.debug('adding column', key, setQuerySource)
            query.columns.push({
              queryColumnId: key,
              queryPath: setQuerySource,
              fieldName: fieldKey
            })
          }
        } as any);
      }
    }

    if (set.columns.length > 0) {
      set.columns.sort((a, b) => a.label.localeCompare(b.label));

      result.push(set);
    }

    if (navigation.navigation) {
      const subedges = Object.keys(edges[navigation.edge].edges).reduce((res, edgeKey) => {
        const node = graphMeta[edges[navigation.edge!].edges[edgeKey].node];
        res[edgeKey] = node;
        return res;
      }, {} as Record<string, Models.GraphMeta.GraphNodeMeta>);
      for (const subnav of navigation.navigation) {
        from(subedges, subnav, querySource.concat(navigation.edge));
      }
    }
  }

  function getColumnLabel(node: string, fieldKey: string, field: Models.GraphMeta.GraphFieldMeta) {

    let result = fieldKey
    if (field.dimensionKey) {
      // Strip dimension key from fieldName
      result = result.substring(0, fieldKey.length - field.dimensionKey.length - 1)
    }

    result = graphPhrases.field(node, result);

    const dimensionKey = field.dimensionKey;
    if (field.dimension && dimensionKey) {
      result += ` (${dimensionKey})`
    }

    return result;
  }
}

function queryPathPhrase(
  graphMeta: Record<string, Models.GraphMeta.GraphNodeMeta>,
  graphPhrases: ReturnType<typeof useGraphPhrases>,
  queryPath: string[]
) {

  const result: string[] = [];

  result.push(graphPhrases.edge('root', queryPath[0]));

  let currentNode = Object.keys(graphMeta).find(r => graphMeta[r].rootEdgeName === queryPath[0])!;
  for (let i = 1; i < queryPath.length; i++) {
    const node = graphMeta[currentNode];
    if (node) {
      result.push(graphPhrases.field(currentNode, queryPath[i]));
      if (i < queryPath.length) {
        currentNode = node.edges[queryPath[i]].node;
      }
    }
  }

  return result;
}

function getColumnNode(
  graphMeta: Record<string, Models.GraphMeta.GraphNodeMeta>,
  queryPath: string[]
) {
  let currentNode = Object.keys(graphMeta).find(r => graphMeta[r].rootEdgeName === queryPath[0])!;
  for (let i = 0; i < queryPath.length; i++) {
    const node = graphMeta[currentNode];
    if (node && i < queryPath.length - 1) {
      currentNode = node.edges[queryPath[i + 1]].node;
    }
  }
  return currentNode;
}
